/**
 * Werbung für die neuen Zahlungsarten (Kevin 31.10.2026): Klarna-Raten, Kauf auf Rechnung für Firmen (Billie),
 * Apple Pay & Co. Zeigt nur, was im Admin → Zahlungsgebühren aktiv ist (GET /public/payment-methods).
 * Beispielraten rechnen die Zahlungsgebühr mit ein (sonst irreführend). Klarna „Pay in 3“ in DE bis 5.000 €.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { liveEvent } from "@/lib/liveEvent";
import LeasingPanel from "@/components/pay/LeasingPanel";
import { useCatalog } from "@/lib/catalog";

export const KLARNA_PAY3_MAX = 5000;
const eur2 = (n) => `${Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

let cache = null;
export function usePayMethods() {
  const [m, setM] = useState(cache?.data || null);
  useEffect(() => {
    if (!cache) cache = { p: api.get("/public/payment-methods").then((r) => { cache.data = r.data || []; return cache.data; }).catch(() => []) };
    let alive = true;
    cache.p.then((d) => { if (alive) setM(d); });
    return () => { alive = false; };
  }, []);
  const by = Object.fromEntries((m || []).map((x) => [x.key, x]));
  return { loaded: m !== null, klarna: by.klarna, billie: by.billie, by };
}

/** Rate bei Klarna „in 3 Raten“ inkl. Zahlungsgebühr; null wenn nicht möglich. */
export function klarnaRate(price, cfg) {
  if (!cfg || !(price > 0)) return null;
  // wie die Kasse (ops/payment_fees.calc_fee): Gebühr = round(Preis × pct + fixed, 2)
  const fee = Math.round((price * Number(cfg.pct || 0) / 100 + Number(cfg.fixed || 0)) * 100) / 100;
  const total = Math.round((price + fee) * 100) / 100;
  if (total > KLARNA_PAY3_MAX) return null;
  return { rate: Math.round((total / 3) * 100) / 100, total, fee };
}

export function KlarnaMark({ className = "" }) {
  return <span className={`inline-flex items-center justify-center whitespace-nowrap rounded-md bg-[#FFA8CD] text-black font-extrabold text-[11px] px-1.5 py-0.5 leading-none ${className}`}>Klarna.</span>;
}

const WALLETS = ["Apple Pay", "Google Pay", "PayPal", "Amazon Pay", "Visa", "Mastercard", "Überweisung"];

// ---------- Leiste ganz oben (im Header, nur deutscher Shop) ----------
export function PayAnnouncementBar() {
  const { klarna, billie } = usePayMethods();
  if (!klarna && !billie) return <div className="h-9 bg-ink" aria-hidden="true" />;
  return (
    <Link to="/zahlungsarten" onClick={() => liveEvent("Zahlarten-Leiste oben angeklickt")} className="h-9 bg-ink text-white text-[13px] flex items-center justify-center gap-2.5 px-4 hover:bg-black transition-colors" data-testid="pay-announcement">
      <span className="rounded-full px-2 py-[2px] text-[10px] font-bold tracking-[0.14em] text-[#1a1206]" style={{ background: "linear-gradient(135deg,#f6cf6b,#e8a83d)" }}>NEU</span>
      <span className="truncate sm:hidden">
        <span>Fotobox in </span><b className="text-[#f6cf6b] font-semibold">Raten</b><span>, auf </span><b className="text-[#f6cf6b] font-semibold">Rechnung</b><span> oder </span><b className="text-[#f6cf6b] font-semibold">Leasing</b>
      </span>
      <span className="truncate hidden sm:inline">
        <span>Fotobox in </span>
        {klarna && <b className="text-[#f6cf6b] font-semibold">3 zinsfreien Raten</b>}
        {klarna && billie && <span>, </span>}
        {billie && <b className="text-[#f6cf6b] font-semibold">auf Rechnung</b>}
        <span> oder per </span><b className="text-[#f6cf6b] font-semibold">Leasing für Firmen</b>
      </span>
      <span className="hidden sm:inline underline underline-offset-4 whitespace-nowrap">So geht's</span>
      <ArrowRight className="w-3.5 h-3.5 sm:hidden shrink-0" />
    </Link>
  );
}

// ---------- Drei Kärtchen unter den Knöpfen im Startseiten-Kopf ----------
export function HeroPayStrip() {
  const { klarna, billie } = usePayMethods();
  const chips = [
    klarna && { k: "klarna", icon: <KlarnaMark />, t: "In 3 Raten zahlen", s: "zinsfrei mit Klarna" },
    billie && { k: "billie", icon: <span className="w-9 h-9 rounded-[10px] grid place-items-center bg-[#eef7f1] text-[#1f8a4c] font-extrabold text-xs">30</span>, t: "Firmen: auf Rechnung", s: "30 Tage Zahlungsziel" },
    { k: "leasing", icon: <span className="w-9 h-9 rounded-[10px] grid place-items-center bg-[#eef2ff] text-[#1d3fd6] font-extrabold text-[10px]">24-84</span>, t: "Leasing & Mietkauf", s: "für Firmen, online anfragen" },
    { k: "wallet", icon: <span className="w-9 h-9 rounded-[10px] grid place-items-center bg-ink text-white font-bold text-[10px]">Pay</span>, t: "Apple Pay & Co.", s: "Google Pay, PayPal, Amazon Pay" },
  ].filter(Boolean);
  if (chips.length < 2) return null;
  return (
    <Link to="/zahlungsarten" onClick={() => liveEvent("Zahlarten-Kärtchen Startseite angeklickt")} className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-w-2xl" data-testid="hero-pay-strip">
      {chips.map((c) => (
        <span key={c.k} className="flex items-center gap-2.5 rounded-2xl border border-hair bg-white/90 px-3.5 py-3 shadow-[0_6px_20px_-14px_rgba(0,0,0,0.35)] hover:border-brand-yellow/50 transition-colors">
          <span className="shrink-0 w-[52px] grid place-items-center">{c.icon}</span>
          <span className="min-w-0 leading-tight whitespace-nowrap">
            <span className="block text-sm font-semibold text-ink">{c.t}</span>
            <span className="block text-xs text-ink-soft">{c.s}</span>
          </span>
        </span>
      ))}
    </Link>
  );
}

// ---------- Großer Bereich „Heute starten. Später bezahlen.“ ----------
export function PayOptionsSection({ exampleSlug = "fotobox-premium", showCta = true, zu = false }) {
  const { klarna, billie, loaded } = usePayMethods();
  const { products } = useCatalog();
  const p = products.find((x) => x.slug === exampleSlug);
  const pkg = p && ((p.packages || []).find((k) => k.recommended) || (p.packages || []).slice(-1)[0]);
  const price = Number(pkg?.price_gross || 0);
  const r = klarnaRate(price, klarna);
  const [lease, setLease] = useState(null);
  const [offen, setOffen] = useState(() => !zu || (typeof window !== "undefined" && window.location.hash === "#bezahlen"));
  useEffect(() => {
    if (!(price > 0)) return undefined;
    let alive = true;
    api.get("/public/leasing/rates", { params: { gross: price } }).then((x) => { if (alive && x.data?.available) setLease(x.data); }).catch(() => {});
    return () => { alive = false; };
  }, [price]);
  if (loaded && !klarna && !billie) return null;
  // Kevin 01.11.2026: als schmale Aufklapp-Zeile am Ende der Startseite (Kunden wollen zuerst die Fotoboxen sehen)
  return (
    <section id="bezahlen" className="relative bg-white py-6 md:py-8" data-testid="pay-options">
      <div className="max-w-[1400px] mx-auto px-4 md:px-10">
       <div className="relative overflow-hidden rounded-2xl bg-ink text-white">
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 10% 50%, rgba(232,168,61,.22), transparent 45%)" }} />
        <button type="button" onClick={() => { setOffen((o) => !o); if (!offen) liveEvent("Zahlarten-Block Startseite aufgeklappt"); }} aria-expanded={offen}
          className="relative w-full flex items-center gap-4 text-left px-5 md:px-7 py-4 md:py-5" data-testid="pay-options-toggle">
          <span className="min-w-0 flex-1">
            <span className="block font-heading text-xl md:text-2xl leading-tight">Heute starten. <span className="font-serif-italic text-[#f6cf6b]">Später bezahlen.</span></span>
            <span className="block text-white/70 text-[13px] md:text-sm mt-1">Raten mit Klarna · Kauf auf Rechnung · Leasing · Apple Pay &amp; Co.</span>
          </span>
          <ChevronDown className={`w-6 h-6 text-[#f6cf6b] shrink-0 transition-transform ${offen ? "rotate-180" : ""}`} aria-hidden />
        </button>
        {offen && (
        <div className="relative px-5 md:px-7 pb-7">
        <p className="text-white/80 text-[15px] max-w-2xl">Ihre Fotobox verdient schon Geld, bevor sie ganz bezahlt ist. Wählen Sie an der Kasse einfach die Zahlungsart, die zu Ihnen passt.</p>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-5">
          {klarna && (
            <div className="rounded-3xl p-6 md:p-7 flex flex-col border" style={{ background: "linear-gradient(180deg, rgba(255,168,205,.16), rgba(255,255,255,.05))", borderColor: "rgba(255,168,205,.45)" }} data-testid="pay-card-klarna">
              <KlarnaMark className="self-start" />
              <h3 className="font-heading text-3xl md:text-[34px] mt-4 leading-tight">In 3 Raten zahlen</h3>
              <p className="mt-2 mb-6 text-white/75 text-[15px] leading-relaxed">Zinsfrei auf drei Monate verteilt. Ihre Fotobox geht sofort in Produktion.</p>
              {r && (
                <div className="mt-auto relative grid grid-cols-3 gap-2">
                  <div aria-hidden className="absolute left-[16%] right-[16%] top-[15px] h-0.5" style={{ background: "linear-gradient(90deg,#FFA8CD,rgba(255,168,205,.25))" }} />
                  {["heute", "in 30 Tagen", "in 60 Tagen"].map((w, i) => (
                    <div key={w} className="relative text-center">
                      <div className={`w-[30px] h-[30px] rounded-full mx-auto mb-2 grid place-items-center text-[13px] font-extrabold ${i ? "bg-[#2a2420] text-[#FFA8CD] border-2 border-[#FFA8CD]" : "bg-[#FFA8CD] text-black"}`}>{i + 1}</div>
                      <div className="font-semibold text-[15px] md:text-base">{eur2(r.rate)}</div>
                      <div className="text-white/60 text-xs">{w}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {billie && (
            <div className="rounded-3xl p-6 md:p-7 flex flex-col border border-white/10 bg-white/[0.06]" data-testid="pay-card-billie">
              <span className="self-start rounded-md bg-[#eef7f1] text-[#1f8a4c] text-[11px] font-extrabold px-2 py-1">Für Firmen</span>
              <h3 className="font-heading text-3xl md:text-[34px] mt-4 leading-tight">Kauf auf Rechnung</h3>
              <p className="mt-2 mb-6 text-white/75 text-[15px] leading-relaxed">Erst liefern, dann zahlen: 30 Tage Zahlungsziel für Unternehmen. Ohne Vorkasse, ohne Papierkram.</p>
              {price > 0 && (
                <div className="mt-auto relative bg-white text-ink rounded-2xl px-4 pt-4 pb-3.5 -rotate-[1.5deg]">
                  <span className="absolute right-3 -top-3.5 rotate-[8deg] border-2 border-[#1f8a4c] text-[#1f8a4c] bg-[#f1fbf5] rounded-lg text-[11px] font-extrabold px-2 py-1 tracking-wide">ZAHLBAR IN 30 TAGEN</span>
                  <div className="flex justify-between gap-3 text-[13px] text-ink-soft py-0.5"><span className="truncate">{p.name} · {pkg.name}</span><span className="whitespace-nowrap">{eur2(price)}</span></div>
                  <div className="flex justify-between text-[13px] text-ink-soft py-0.5"><span>Versand Deutschland</span><span>inklusive</span></div>
                  <div className="flex justify-between text-[13px] font-bold border-t border-hair mt-1.5 pt-2"><span>Heute zu zahlen</span><span>0,00 €</span></div>
                </div>
              )}
            </div>
          )}
          {lease?.rows?.length > 0 && (
            <div className="rounded-3xl p-6 md:p-7 flex flex-col border bg-white/[0.06]" style={{ borderColor: "rgba(120,150,255,.45)" }} data-testid="pay-card-leasing">
              <span className="self-start rounded-md bg-[#eef2ff] text-[#1d3fd6] text-[11px] font-extrabold px-2 py-1">Für Firmen</span>
              <h3 className="font-heading text-3xl md:text-[34px] mt-4 leading-tight">Leasing oder Mietkauf</h3>
              <p className="mt-2 mb-6 text-white/75 text-[15px] leading-relaxed">Feste Monatsrate über 24 bis 84 Monate. Beim Mietkauf gehört die Fotobox nach der letzten Rate Ihnen. Direkt auf der Produktseite anfragen.</p>
              <div className="mt-auto rounded-2xl bg-white text-ink px-4 py-3">
                <div className="text-[12px] text-ink-soft">{p?.name} {pkg?.name} ab</div>
                <div className="font-heading text-3xl leading-tight">{eur2(lease.rows[lease.rows.length - 1].leasing)} <span className="text-sm font-body text-ink-soft">netto im Monat</span></div>
                <div className="mt-2 flex items-end gap-1.5 h-12">
                  {lease.rows.map((r) => (
                    <div key={r.m} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full rounded-t bg-[#c7d2fe]" style={{ height: `${Math.max(14, Math.round((r.leasing / lease.rows[0].leasing) * 40))}px` }} />
                      <span className="text-[9px] text-ink-muted">{r.m}</span>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-ink-muted mt-0.5 text-center">Laufzeit in Monaten</div>
              </div>
            </div>
          )}
          <div className="rounded-3xl p-6 md:p-7 flex flex-col border border-white/10 bg-white/[0.06]" data-testid="pay-card-wallets">
            <span className="self-start rounded-md bg-white text-ink text-[11px] font-extrabold px-2 py-1">Sofort</span>
            <h3 className="font-heading text-3xl md:text-[34px] mt-4 leading-tight">Mit einem Fingertipp</h3>
            <p className="mt-2 mb-6 text-white/75 text-[15px] leading-relaxed">Bezahlen Sie in Sekunden mit Face ID, Touch ID oder Ihrem bestehenden Konto.</p>
            <div className="mt-auto flex flex-wrap gap-2">
              {WALLETS.map((w) => <span key={w} className="bg-white text-ink rounded-lg px-3 py-2 text-sm font-bold">{w}</span>)}
            </div>
          </div>
        </div>
        {showCta && <Link to="/fotoboxen" className="btn-accent mt-8 !py-4 !px-7 inline-flex" data-testid="pay-options-cta">Jetzt Fotobox auswählen <ArrowRight className="w-4 h-4" /></Link>}
        <p className="mt-5 text-xs text-white/55 max-w-4xl leading-relaxed">
          {r ? `Beispielraten für ${p.name} ${pkg.name} inkl. Zahlungsentgelt. ` : ""}
          Ratenzahlung über Klarna bis {KLARNA_PAY3_MAX.toLocaleString("de-DE")} € Bestellwert. Rechnungskauf, Leasing und Mietkauf nur für Unternehmen, Leasingraten netto zzgl. MwSt., jeweils nach Prüfung durch den Anbieter.
        </p>
        </div>
        )}
       </div>
      </div>
    </section>
  );
}


// ---------- Leasing / Mietkauf über Miete24 (grenke), Kevin 31.10.2026 ----------
// Faktoren = Monatsrate / Anschaffungswert netto, aus der Miete24-Beispielkalkulation (Magic Mirror 3.857,14 € netto,
// Produktsektor Foto- und Videotechnik). Nur Richtwerte: das verbindliche Angebot erstellt Kevin im Miete24-Portal.
export const LEASE_BASE = 3857.14;
export const LEASE_RATES = [
  // [Monate, Leasing Vollamortisation, Mietkauf]
  [24, 179.31, 191.05], [36, 123.71, 131.54], [48, 97.48, 103.36], [54, 88.48, 93.57],
  [60, 81.04, 85.35], [72, 69.69, 73.21], [84, 61.86, 64.99],
];
export const LEASE_MIN_NET = 1000; // kleinere Beträge nicht bewerben
export function leaseTable(gross) {
  const net = Math.round((gross / 1.19) * 100) / 100;
  if (!(net >= LEASE_MIN_NET)) return null;
  const f = (r) => Math.round((r / LEASE_BASE) * net * 100) / 100;
  return { net, rows: LEASE_RATES.map(([m, va, mk]) => ({ m, leasing: f(va), mietkauf: f(mk) })) };
}

// ---------- Block unter dem Preis auf der Produktseite (Zeilen zum Aufklappen, Kevin 31.10.2026) ----------
function Row({ id, open, setOpen, head, children, tint, onTrack }) {
  const isOpen = open === id;
  return (
    <div className="border-t border-hair first:border-t-0" style={tint && !isOpen ? { background: tint } : undefined}>
      <button type="button" onClick={() => { setOpen(isOpen ? null : id); if (!isOpen && onTrack) onTrack(id); }} aria-expanded={isOpen}
        className="w-full flex items-center gap-3 px-4 py-3 text-left" style={tint && isOpen ? { background: tint } : undefined} data-testid={`paybox-${id}`}>
        {head}
        <ChevronDown className={`w-4 h-4 ml-auto shrink-0 text-ink-muted transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && <div className="px-4 pb-4 pt-1" data-testid={`paybox-${id}-details`}>{children}</div>}
    </div>
  );
}

const Steps = ({ items }) => (
  <ol className="mt-3 space-y-1.5 text-[13px] text-ink-soft">
    {items.map((x, i) => (
      <li key={i} className="flex gap-2"><span className="shrink-0 w-5 h-5 rounded-full bg-cream border border-hair text-[11px] font-bold text-ink grid place-items-center">{i + 1}</span><span>{x}</span></li>
    ))}
  </ol>
);

export function ProductPayBox({ price, productName = "" }) {
  const { klarna, billie } = usePayMethods();
  const [open, setOpen] = useState(null);
  const lt = leaseTable(price);
  const track = (id) => liveEvent({ klarna: "Klarna-Raten angesehen", billie: "Rechnungskauf angesehen", leasing: "Leasing/Mietkauf angesehen" }[id] || id, `${productName} · ${eur2(price)}`);
  if (!(price > 0) || (!klarna && !billie && !lt)) return null;
  const r = klarnaRate(price, klarna);
  const due = new Date(Date.now() + 30 * 864e5).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  return (
    <div className="mt-4 rounded-2xl border border-hair overflow-hidden" data-testid="product-paybox">
      {klarna && (
        <Row id="klarna" open={open} setOpen={setOpen} onTrack={track} tint="linear-gradient(90deg, rgba(255,168,205,.18), #fff 70%)"
          head={<>
            <KlarnaMark className="!text-[12px] !px-2 !py-1.5 shrink-0" />
            <span className="min-w-0 leading-tight">
              <b className="block text-[15px] text-ink">{r ? `oder 3 × ${eur2(r.rate)} zinsfrei` : "Mit Klarna später oder in Raten zahlen"}</b>
              <span className="block text-[13px] text-ink-soft">{r ? "Heute nur die erste Rate · So funktioniert's" : "Finanzierung mit längerer Laufzeit möglich"}</span>
            </span>
          </>}>
          {r && (
            <div className="relative grid grid-cols-3 gap-2 rounded-xl bg-ink text-white px-3 pt-4 pb-3">
              <div aria-hidden className="absolute left-[18%] right-[18%] top-[30px] h-0.5" style={{ background: "linear-gradient(90deg,#FFA8CD,rgba(255,168,205,.25))" }} />
              {["heute", "in 30 Tagen", "in 60 Tagen"].map((w, i) => (
                <div key={w} className="relative text-center">
                  <div className={`w-7 h-7 rounded-full mx-auto mb-1.5 grid place-items-center text-[12px] font-extrabold ${i ? "bg-[#2a2420] text-[#FFA8CD] border-2 border-[#FFA8CD]" : "bg-[#FFA8CD] text-black"}`}>{i + 1}</div>
                  <div className="font-semibold text-sm">{eur2(r.rate)}</div>
                  <div className="text-white/60 text-[11px]">{w}</div>
                </div>
              ))}
            </div>
          )}
          <Steps items={[
            "In der Kasse „Klarna“ wählen.",
            r ? "Klarna zeigt Ihnen Ihre Möglichkeiten: in 3 zinsfreien Raten, in 30 Tagen oder als Finanzierung mit längerer Laufzeit." : `In 3 Raten geht bis ${KLARNA_PAY3_MAX.toLocaleString("de-DE")} € Bestellwert. Darüber zeigt Ihnen Klarna eine Finanzierung mit längerer Laufzeit, falls möglich.`,
            "Sobald Klarna bestätigt, geht Ihre Fotobox in Produktion. Die Raten zahlen Sie direkt an Klarna.",
          ]} />
          <p className="mt-3 text-[11px] text-ink-muted">Für Privatpersonen ab 18 Jahren, nach Prüfung durch Klarna.{r ? " Raten inkl. Zahlungsentgelt." : " Bei einer Finanzierung können Zinsen anfallen, die Klarna Ihnen vorher anzeigt."} <Link to="/zahlungsarten" className="underline">Mehr erfahren</Link></p>
        </Row>
      )}
      {billie && (
        <Row id="billie" open={open} setOpen={setOpen} onTrack={track}
          head={<>
            <span className="shrink-0 rounded-md bg-[#eef7f1] text-[#1f8a4c] font-extrabold text-[11px] px-2 py-1.5">30 T.</span>
            <span className="min-w-0 leading-tight">
              <b className="block text-[15px] text-ink">Für Firmen: Kauf auf Rechnung</b>
              <span className="block text-[13px] text-ink-soft">Erst nach 30 Tagen bezahlen · So funktioniert's</span>
            </span>
          </>}>
          <div className="relative bg-white border border-hair rounded-xl px-4 pt-4 pb-3 shadow-sm">
            <span className="absolute right-3 -top-3 rotate-[6deg] border-2 border-[#1f8a4c] text-[#1f8a4c] bg-[#f1fbf5] rounded-md text-[10px] font-extrabold px-2 py-0.5 tracking-wide">ZAHLBAR IN 30 TAGEN</span>
            <div className="flex justify-between text-[13px] font-bold"><span>Heute zu zahlen</span><span>0,00 €</span></div>
            <div className="flex justify-between text-[13px] text-ink-soft mt-1"><span>Fällig etwa am {due}</span><span>Rechnungsbetrag</span></div>
          </div>
          <Steps items={[
            "In der Kasse Ihren Firmennamen eintragen.",
            "„Kauf auf Rechnung (Geschäftskunden)“ wählen. Unser Partner für Rechnungskauf prüft Ihr Unternehmen in wenigen Sekunden.",
            "Wir starten sofort mit Ihrer Bestellung. Sie zahlen die Rechnung innerhalb von 30 Tagen an unseren Partner.",
          ]} />
          <p className="mt-3 text-[11px] text-ink-muted">Nur für Unternehmen und Selbstständige, nicht für Privatpersonen. Unser Partner entscheidet im Einzelfall. <Link to="/faq" className="underline">Voraussetzungen</Link></p>
        </Row>
      )}
      {lt && (
        <Row id="leasing" open={open} setOpen={setOpen} onTrack={track}
          head={<>
            <span className="shrink-0 rounded-md bg-[#eef2ff] text-[#1d3fd6] font-extrabold text-[11px] px-2 py-1.5">Leasing</span>
            <span className="min-w-0 leading-tight">
              <b className="block text-[15px] text-ink">Für Firmen: Leasing ab {eur2(lt.rows[lt.rows.length - 1].leasing)} netto im Monat</b>
              <span className="block text-[13px] text-ink-soft">Oder Mietkauf, 24 bis 84 Monate · Direkt online anfragen</span>
            </span>
          </>}>
          <LeasingPanel price={price} productName={productName} fallback={lt} />
        </Row>
      )}
      <div className="flex flex-wrap gap-1.5 px-4 py-2.5 bg-cream border-t border-hair">
        {WALLETS.map((w) => <span key={w} className="border border-hair bg-white rounded-md px-2 py-0.5 text-[11px] font-bold text-ink">{w}</span>)}
      </div>
    </div>
  );
}
